import { DEFAULT_AVATAR_URL } from '@constants/avatar'
import { Avatar, Stack, Text, XStack, YStack } from 'tamagui'
import { HeroIcon } from '@components/HeroIcon'
import { Button } from '../Button'
import { useCallback } from 'react'
import { LinearGradient } from 'expo-linear-gradient'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { router, usePathname } from 'expo-router'
import { openWebBrowser } from '@utils/openWebBrowser'
import { MEMBERS_PAGE_CERTIFICATES } from '@constants/features'
import { useDomain, useHasFeature } from '@hooks/useThemeParams'
import { useSession } from '@hooks/useSession'

const ICON_SIZE = 24

export function BottomNavBar({ openModal }: { openModal: () => void }) {
  const { user } = useSession()
  const { subdomainURL } = useDomain()
  const hasCertificatesPage = useHasFeature(MEMBERS_PAGE_CERTIFICATES)

  const handleGoHome = useCallback(() => {
    router.navigate('/')
  }, [])

  // No app a lista de certificados é rota nativa: mandar para o navegador
  // jogava o aluno para fora. Na web a navegação continua sendo load completo,
  // porque a flag é gate de nginx e um push dentro da SPA passaria por cima
  // dela. Sem a flag, o caminho antigo continua valendo nas duas plataformas.
  const handleGoToCertificates = useCallback(() => {
    if (hasCertificatesPage) {
      router.navigate('/certificates')
      return
    }

    openWebBrowser(`${subdomainURL}/certificates`)
  }, [subdomainURL, hasCertificatesPage])

  return (
    <Stack paddingTop="$spacing-inset-xs">
      <LinearGradient
        colors={['transparent', 'rgba(0,0,0,0.76)']}
        style={gradientStyle}
      />
      <XStack
        marginHorizontal="$spacing-inset-xs"
        bg="$bg-chrome"
        paddingTop="$spacing-inset-quarck"
        borderRadius="$border-radius-pill"
        paddingHorizontal="$spacing-inset-md"
        height={65}
      >
        <NavButton
          label="INÍCIO"
          action={handleGoHome}
          icon="house"
          user={user}
        />
        <NavButton
          label="CERTIFICADOS"
          action={handleGoToCertificates}
          icon="user-graduate"
          user={user}
        />
        <NavButton
          label="PERFIL"
          avatar={user?.coverImageUrl}
          action={openModal}
          icon="user"
          user={user}
        />
      </XStack>
    </Stack>
  )
}

interface INavButtonProps {
  icon: IconName
  label: string
  avatar?: string
  action: () => void
  user?: {
    firstName?: string
  } | null
}

function NavButton({ icon, label, action, avatar, user }: INavButtonProps) {
  const pathname = usePathname()
  const isActive = pathname === '/' && label === 'INÍCIO'

  const hasAvatar =
    avatar !== null && avatar !== undefined && avatar !== DEFAULT_AVATAR_URL

  return (
    // `flexBasis={0}` deixa as três colunas com a mesma largura
    // independentemente do tamanho do rótulo, e o traço de ativo cobre a coluna
    // inteira.
    <Stack
      flex={1}
      flexBasis={0}
      borderTopWidth={isActive ? 2 : 0}
      borderTopColor="$accent"
    >
      {/* `icon` mantém o conteúdo fora do <Text> interno do Button. No nativo,
          ícone e rótulo dentro de um Text viram anexo inline: não recebem
          toque e são posicionados pela linha de base, não pela coluna. Sem
          padding horizontal o rótulo mais longo (CERTIFICADOS) cabe sem
          reticências. */}
      <Button
        icon
        size="small"
        width="100%"
        height="100%"
        paddingHorizontal={0}
        borderWidth={0}
        pressStyle={pressStyle}
        onPress={action}
        accessibilityRole="button"
        accessibilityLabel={label}
      >
        <YStack
          flex={1}
          alignItems="center"
          justifyContent="center"
          gap="$spacing-inset-quarck"
        >
          {avatar && icon === 'user' ? (
            <Avatar
              circular
              size={ICON_SIZE}
              borderColor="$icon-secondary"
              borderWidth={1}
            >
              {hasAvatar ? (
                <Avatar.Image src={avatar} />
              ) : (
                <Avatar.Fallback
                  backgroundColor="$icon-secondary"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text
                    color="$text-on-accent"
                    fontSize="$font-size-xs"
                    textAlign="center"
                    fontWeight={700}
                  >
                    {user?.firstName?.[0]?.toUpperCase()}
                  </Text>
                </Avatar.Fallback>
              )}
            </Avatar>
          ) : (
            <HeroIcon
              icon={['fal', icon]}
              color={isActive ? '$accent' : '$icon-primary'}
              size={ICON_SIZE}
            />
          )}
          <Text
            ff={isActive ? '$mulishBold' : '$mulishRegular'}
            color={isActive ? '$accent' : '$text-secondary'}
            fontSize="$font-size-xxxs"
            fontWeight={isActive ? 700 : 400}
            numberOfLines={1}
            maxFontSizeMultiplier={1}
            width="100%"
            textAlign="center"
          >
            {label}
          </Text>
        </YStack>
      </Button>
    </Stack>
  )
}

const gradientStyle = {
  position: 'absolute' as const,
  left: 0,
  right: 0,
  top: 0,
  height: 115,
}

const pressStyle = { opacity: 0.6 }
